import React, { useRef, useState } from "react";
import { X } from "lucide-react";
import { useToast } from "@chakra-ui/react";
import { TicketAttachment } from "../../types/ticket";
import { uploadSupportAttachment } from "../../apis/feedback/upload.support.attachment";
import { isVideoAttachment } from "./attachment-gallery";

// Shared attachment composer state: multiple image/video uploads to R2, with a
// hidden file input, an uploading flag, and removable previews. Used by both the
// support chat composer and the ticket thread composer.
export function useAttachmentUpload() {
  const toast = useToast({ position: "top", containerStyle: { zIndex: 9 } });
  const [pending, setPending] = useState<TicketAttachment[]>([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const fileRef = useRef<HTMLInputElement>(null);

  const pick = () => fileRef.current?.click();

  const onFilesSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    if (e.target) e.target.value = "";
    if (!files.length) return;
    setUploadingCount((c) => c + files.length);
    files.forEach((file, i) => {
      const form = new FormData();
      form.append("file", file);
      uploadSupportAttachment({
        params: "upload",
        // Unique per upload so parallel multipart requests aren't collapsed by
        // the base-API request-dedup ring (FormData bodies hash identically).
        query: { _u: `${Date.now()}-${i}-${Math.round(Math.random() * 1e6)}` },
        body: form,
        onCompleted({ data }) {
          setPending((p) => [
            ...p,
            {
              url: data.url,
              provider: data.provider,
              filename: data.filename,
              contentType: data.contentType,
              size: data.size,
            },
          ]);
          setUploadingCount((c) => Math.max(0, c - 1));
        },
        onError(err) {
          setUploadingCount((c) => Math.max(0, c - 1));
          toast({
            status: "error",
            description: typeof err === "string" ? err : "Upload failed",
          });
        },
      });
    });
  };

  const removeAt = (i: number) =>
    setPending((p) => p.filter((_, idx) => idx !== i));
  const reset = () => setPending([]);

  return {
    pending,
    uploading: uploadingCount > 0,
    fileRef,
    pick,
    onFilesSelected,
    removeAt,
    reset,
  };
}

export const HiddenFileInput: React.FC<{
  fileRef: React.RefObject<HTMLInputElement>;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
}> = ({ fileRef, onChange }) => (
  <input
    ref={fileRef}
    type="file"
    accept="image/*,video/*"
    multiple
    hidden
    onChange={onChange}
  />
);

export const PendingPreviews: React.FC<{
  items: TicketAttachment[];
  onRemove: (i: number) => void;
}> = ({ items, onRemove }) =>
  items.length ? (
    <div className="flex gap-2 flex-wrap mb-2">
      {items.map((a, i) => (
        <div key={i} className="relative">
          {isVideoAttachment(a) ? (
            <video
              src={a.url}
              muted
              className="w-14 h-14 object-cover rounded-md"
            />
          ) : (
            <img
              src={a.url}
              alt={a.filename}
              className="w-14 h-14 object-cover rounded-md"
            />
          )}
          <button
            onClick={() => onRemove(i)}
            className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center"
          >
            <X size={10} />
          </button>
        </div>
      ))}
    </div>
  ) : null;
